import { useEffect, useMemo, useRef, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import { useLazyGetWorldFleetQuery } from 'app/api/facade';
import { WorldFleetDataItem, WorldFleetDataItemByType } from 'ais-facade-api/api';
import { Button, Icon, Input, PageHeader, Table, Typo } from 'app/components';
import { getDateRange } from 'helpers/getDateRange';
import { columns as tableColumns } from './columns';
import { VesselTypesMap } from 'app/constants/_constants';
import { VesselTypeName } from 'app/types/types';
import { useOnClickOutside } from 'app/hooks/useOnClickOutside';
import { WorldFleetFilter } from 'app/components/FilterPanel/WorldFleetFilter';
import styles from './WorldFleetPage.module.scss';

export interface WorldFleetTableRow {
  countryCode?: string;
  countryName?: string;
  fleet: { typeName: VesselTypeName; count: number }[];
  total: number;
  percentage: number;
}

function groupByType(fleetArray: WorldFleetDataItemByType[]): { typeName: VesselTypeName; count: number }[] {
  const grouped: Record<VesselTypeName, { count: number }> = {} as Record<VesselTypeName, { count: number }>;

  fleetArray.forEach((item) => {
    const typeGroup = findTypeGroup(item.type);

    if (!grouped[typeGroup]) {
      grouped[typeGroup] = {
        count: 0,
      };
    }
    grouped[typeGroup].count += item.count || 0;
  });

  return Object.keys(grouped).map((key) => ({
    typeName: key as VesselTypeName,
    count: grouped[key as VesselTypeName].count,
  }));
}

function findTypeGroup(type?: number): VesselTypeName {
  for (const [key, value] of Array.from(VesselTypesMap.entries())) {
    if (value.codes.includes(type)) {
      return key;
    }
  }
  return 'Other';
}

export const WorldFleetPage = () => {
  const { t } = useTranslation();
  const language = useMemo(() => getI18n().language, [t]);

  const [getFleet, { data: fleet, isFetching }] = useLazyGetWorldFleetQuery();

  const datetime = getDateRange().new_today;

  const [term, setTerm] = useState<string>('');
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>();

  const filterPanelRef = useRef<HTMLDivElement>(null);
  const clickOutsideHandler = () => {
    setShowFilter(false);
  };
  useOnClickOutside(filterPanelRef, clickOutsideHandler);

  const allFleetTotal: number = useMemo(() => {
    if (!fleet?.data?.items) return 0;

    const dataItems = fleet.data.items;
    return dataItems.reduce((acc, cur) => {
      const totalByType = cur.byType?.reduce((acc, cur) => (typeof cur.count === 'number' ? cur.count + acc : acc), 0);
      return typeof totalByType === 'number' ? acc + totalByType : acc;
    }, 0);
  }, [fleet]);

  const tableDataTotal: number = useMemo(() => {
    if (!fleet?.data?.items) return 0;

    const dataItems =
      filter?.touched && filter?.country?.length > 0
        ? fleet.data.items.filter(({ countryCode }) => filter.country?.includes(countryCode))
        : fleet.data.items;
    return dataItems.reduce((acc, cur) => {
      const totalByType = cur.byType?.reduce((acc, cur) => (typeof cur.count === 'number' ? cur.count + acc : acc), 0);
      return typeof totalByType === 'number' ? acc + totalByType : acc;
    }, 0);
  }, [fleet, filter]);

  const tableData: WorldFleetTableRow[] | undefined = useMemo(() => {
    const groupedByType: WorldFleetTableRow[] = [];
    if (!fleet?.data?.items) return;
    const dataItems =
      filter?.touched && filter?.country?.length > 0
        ? fleet.data.items.filter(({ countryCode }) => filter.country?.includes(countryCode))
        : fleet.data.items;

    for (const country of dataItems) {
      const { countryCode, countryName, byType } = country;
      if (byType) {
        const fleetGroupped = groupByType(byType);

        const total = Object.values(fleetGroupped).reduce((acc, cur) => acc + cur.count, 0);
        const percentage = (total / allFleetTotal) * 100;

        groupedByType.push({ countryCode, countryName, fleet: fleetGroupped, total, percentage });
      }
    }

    // оставляем пока разбивку на типы, потому что потом должны уметь расписывать долю флота по каждому
    return groupedByType;
  }, [fleet, allFleetTotal, filter]);

  const filteredFleet: WorldFleetDataItem[] | undefined = useMemo(() => {
    if (term.length === 1 || !term) {
      return tableData;
    }

    return tableData?.filter((vessel: WorldFleetDataItem) => {
      const countryNameMatches = vessel?.countryName?.toLowerCase().includes(term.toLowerCase());
      const countryCodeMatches = vessel?.countryCode?.toLowerCase().includes(term.toLowerCase());
      return countryNameMatches || countryCodeMatches;
    });
  }, [tableData, term]);

  useEffect(() => {
    getFleet({ datetime });
  }, [language]);

  return (
    <div className={styles.page}>
      <PageHeader title={t('WorldFleet')} iconName="world-fleet" size="24px" />

      <div className={styles.table_actions}>
        <div className={styles.search_container}>
          <Typo
            value={`${t('FleetVesselsTotal')}${filter?.touched ? ` ${t('AppliedFilters')}:` : ':'} ${typeof tableDataTotal === 'number' ? tableDataTotal.toLocaleString('ru-RU') : '–'}`}
            size="m"
          />
          <Input
            className={styles.table_search}
            type="search"
            onChange={(e) => {
              setTerm(e.target.value);
            }}
            placeholder={t('SearchPlaceholder')}
            value={term}
          />
        </div>
        <Button variant="general" onClick={() => setShowFilter(true)} className={styles.icon_button}>
          {filter?.touched ? <Icon name="filter-active" /> : <Icon name="filter" />}

          <Typo value={t('Filter')} variant="p" />
        </Button>
      </div>
      <div className={styles.fleet_table}>
        {isFetching && <span className={styles.loading}>{t('SearchingText')}</span>}

        {!isFetching && filteredFleet && filteredFleet.length > 0 && tableData && (
          <div className={styles.table_container}>
            <Table data={filteredFleet} columns={tableColumns} withFooter />
          </div>
        )}
        {!filteredFleet || (filteredFleet?.length === 0 && <div>{t('NoResultsFound')}</div>)}
      </div>
      {showFilter && (
        <WorldFleetFilter
          onClose={() => {
            setShowFilter(false);
          }}
          filters={filter}
          handleFilters={setFilter}
          innerRef={filterPanelRef}
        />
      )}
    </div>
  );
};
